/**
 * LER E ESCREVER INSTITUIÇÃO E VÍNCULO.
 *
 * A regra de quem pode o quê mora em `firestore.rules`. Este arquivo só monta os documentos no
 * formato que a regra espera e devolve erro legível quando ela recusa — a mesma divisão que o
 * `lib/instituicao/api.ts` do EducaHubPlay adotou, e pela razão que as regras da biblioteca já
 * pagaram aqui: **o cliente pode mentir sobre qualquer campo que ele mesmo grava.**
 *
 * Toda função devolve `Resultado` em vez de lançar. Numa plataforma usada em rede de escola, erro
 * de rede é rotina, e uma tela que quebra porque o pedido de vínculo de um aluno não subiu é pior
 * do que uma que diz "não deu, tente de novo".
 */
import { where } from "firebase/firestore";
import Firebase from "../api/firebase.class";
import {
  Instituicao,
  MS_ATE_VOLTAR_A_FILA,
  PapelInstituicao,
  SituacaoInstituicao,
  SituacaoVinculo,
  Vinculo,
  idDoVinculo,
} from "./modelo";

export interface Resultado<T = void> {
  ok: boolean;
  dado?: T;
  erro?: string;
}

/** Traduz o erro do Firestore para algo que a pessoa entenda. */
function legivel(e: unknown, contexto: string): string {
  const codigo =
    e && typeof e === "object" && "code" in e ? String((e as { code: unknown }).code) : "";
  if (codigo.includes("permission-denied")) {
    return `Você não tem permissão para ${contexto}.`;
  }
  if (codigo.includes("unavailable") || codigo.includes("network")) {
    return "Sem conexão com o servidor agora. Tente de novo em instantes.";
  }
  return e instanceof Error ? e.message : String(e);
}

/* ─────────────────────────── instituição ─────────────────────────── */

/**
 * Pede a criação de uma instituição. Ela nasce `solicitada`; ativar é da equipe.
 *
 * `T2.74`: *"caso não exista uma instituição, ele vai lá, escreveu o nome daquela instituição e
 * aquilo vai cair numa solicitação para que a equipe que gere o HQ-Lab verifique se aquela
 * instituição de fato existe e se você de fato está apto a ser um moderador daquela instituição."*
 *
 * Não há como nascer `ativa` — e a regra do Firestore afirma o mesmo. Se desse, o provisionamento
 * viraria decoração.
 */
export async function solicitarInstituicao(entrada: {
  id: string;
  nome: string;
  apelido?: string;
  cidade?: string;
  uf?: string;
  solicitanteUid: string;
  solicitanteEmail?: string;
}): Promise<Resultado<string>> {
  try {
    await Firebase.setDocument("instituicoes", entrada.id, {
      nome: entrada.nome.trim(),
      apelido: entrada.apelido?.trim() || null,
      cidade: entrada.cidade?.trim() || null,
      uf: entrada.uf?.trim().toUpperCase() || null,
      situacao: "solicitada",
      solicitanteUid: entrada.solicitanteUid,
      solicitanteEmail: entrada.solicitanteEmail ?? null,
      criadaEm: new Date().toISOString(),
    });
    return { ok: true, dado: entrada.id };
  } catch (e) {
    return { ok: false, erro: legivel(e, "pedir o cadastro de uma instituição") };
  }
}

export async function buscarInstituicao(id: string): Promise<Instituicao | null> {
  try {
    return (await Firebase.getDocument("instituicoes", id)) as Instituicao | null;
  } catch {
    return null;
  }
}

/**
 * As instituições ATIVAS, para a pessoa achar a escola dela.
 *
 * `T2.74`: *"ele tem que ter uma parte em que mostra TODAS AS INSTITUIÇÕES QUE JÁ EXISTEM."*
 *
 * O filtro `situacao == "ativa"` não é só produto: contra a regra publicada, uma listagem sem
 * filtro é negada. E é também o certo — escola que a equipe ainda não confirmou não deve aparecer
 * como opção para um aluno escolher.
 */
export async function listarInstituicoesAtivas(): Promise<Instituicao[]> {
  try {
    const docs = await Firebase.getCollection("instituicoes", [where("situacao", "==", "ativa")], 500);
    return (docs as Instituicao[]).sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  } catch {
    return [];
  }
}

/** As que EU pedi e ainda não foram confirmadas — para a pessoa saber que o pedido está de pé. */
export async function minhasSolicitacoesDeInstituicao(uid: string): Promise<Instituicao[]> {
  try {
    const docs = await Firebase.getCollection(
      "instituicoes",
      [where("solicitanteUid", "==", uid)],
      50
    );
    return (docs as Instituicao[]).filter((i) => i.situacao !== "ativa");
  } catch {
    return [];
  }
}

/* ─────────────────────────── vínculo ─────────────────────────── */

/**
 * Pede vínculo. Nasce `pendente` sempre — inclusive o de educador.
 *
 * `T2.78`: *"cai numa caixa de solicitação desse moderador, ISSO PRECISA SER COMPOSTO COMO UM
 * PEDIDO."* E `T2.79` diz o que a aprovação decide, e o que ela NÃO decide: *"o aluno em teoria
 * poderia entrar independentemente. Ele só não vai ser associado àquela biblioteca específica."*
 * Ou seja, isto é filtro de biblioteca, não portão de cadastro.
 */
export async function solicitarVinculo(entrada: {
  instituicaoId: string;
  /** O nome oficial, da base: o estudante pode pedir antes de a instituição existir no banco. */
  instituicaoNome?: string;
  uid: string;
  nome?: string;
  email?: string;
  papel: PapelInstituicao;
}): Promise<Resultado<string>> {
  const id = idDoVinculo(entrada.instituicaoId, entrada.uid);
  try {
    await Firebase.setDocument("vinculos", id, {
      instituicaoId: entrada.instituicaoId,
      instituicaoNome: entrada.instituicaoNome ?? null,
      uid: entrada.uid,
      nome: entrada.nome ?? null,
      email: entrada.email ?? null,
      papel: entrada.papel,
      situacao: "pendente" as SituacaoVinculo,
      criadoEm: new Date().toISOString(),
    });
    return { ok: true, dado: id };
  } catch (e) {
    return { ok: false, erro: legivel(e, "pedir vínculo com esta instituição") };
  }
}

/** Meus vínculos — uma pessoa pode estar em mais de uma instituição (`T2.76`). */
export async function meusVinculos(uid: string): Promise<Vinculo[]> {
  try {
    const docs = await Firebase.getCollection("vinculos", [where("uid", "==", uid)], 100);
    return docs as Vinculo[];
  } catch {
    return [];
  }
}

/**
 * A fila de moderação: quem pediu para entrar.
 *
 * `T2.75`: *"todas as solicitações daquela instituição vão cair na mão desse moderador e de
 * outros, por exemplo, outros educadores."* Só educador ativo consegue ler — e quem garante isso
 * é a regra, não este filtro.
 */
export async function vinculosDaInstituicao(
  instituicaoId: string,
  situacao?: SituacaoVinculo
): Promise<Vinculo[]> {
  try {
    const filtros = [where("instituicaoId", "==", instituicaoId)];
    if (situacao) filtros.push(where("situacao", "==", situacao));
    const docs = await Firebase.getCollection("vinculos", filtros, 300);
    return (docs as Vinculo[]).sort((a, b) => (a.criadoEm ?? "").localeCompare(b.criadoEm ?? ""));
  } catch {
    return [];
  }
}

/**
 * Aprova, recusa ou remove um vínculo.
 *
 * `moderadoPor` e `moderadoEm` não são telemetria: sem eles, moderação não tem responsável. Numa
 * plataforma em que a decisão é sobre o acesso de uma criança, "quem aprovou" é a pergunta que
 * alguém vai fazer.
 */
export async function moderarVinculo(
  vinculoId: string,
  situacao: Extract<SituacaoVinculo, "ativo" | "recusado" | "removido">,
  moderadorUid: string
): Promise<Resultado> {
  try {
    const agora = new Date().toISOString();
    await Firebase.updateDocument("vinculos", vinculoId, {
      situacao,
      moderadoPor: moderadorUid,
      moderadoEm: agora,
      // Caso F (regras de 06/10): a recusa leva a hora, que o banco confere; dela contam os 7 dias.
      ...(situacao === "recusado" ? { recusadoEmMs: Date.now() } : {}),
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, erro: legivel(e, "moderar este vínculo") };
  }
}

/** Pedir de novo depois de recusado ou removido (`T2.70`). Volta para a fila. */
export async function pedirDeNovo(vinculo: Vinculo): Promise<Resultado> {
  try {
    // 08/10: educador que pede de novo numa instituição RECUSADA a reivindica de novo.
    if (vinculo.papel === "educador") {
      const inst = await buscarInstituicao(vinculo.instituicaoId);
      if (inst?.situacao === "recusada") {
        await Firebase.updateDocument("instituicoes", vinculo.instituicaoId, {
          situacao: "solicitada",
          solicitanteUid: vinculo.uid,
        });
      }
    }
    await Firebase.updateDocument("vinculos", vinculo.id, {
      situacao: "pendente" as SituacaoVinculo,
      papel: vinculo.papel,
      criadoEm: new Date().toISOString(),
      // Caso F: depois de RECUSA, o pedido só aparece na fila 7 dias depois dela (o banco confere).
      ...(vinculo.situacao === "recusado"
        ? { visivelEmMs: (vinculo.recusadoEmMs ?? 0) + MS_ATE_VOLTAR_A_FILA }
        : {}),
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, erro: legivel(e, "pedir vínculo de novo") };
  }
}

/* ─────────────── a base oficial e o portão (instituicoes-ensino, regras de 06/10) ─────────────── */

/** Uma linha da base do INEP, no formato da busca de `instituicoes-ensino.mjs`. */
export interface InstituicaoDaBase {
  id: string;
  nivel: string;
  codigo: string;
  nome: string;
  sigla?: string;
  municipio: string;
  rede: string;
  tipo: string;
}

/** A situação de cada instituição da base no HQ-Lab. Sem documento no banco, ela é "inexistente". */
export async function situacoesNoHqLab(
  ids: string[]
): Promise<Record<string, SituacaoInstituicao | "inexistente">> {
  const pares = await Promise.all(
    ids.map(async (id) => [id, (await buscarInstituicao(id))?.situacao ?? "inexistente"] as const)
  );
  return Object.fromEntries(pares);
}

/**
 * Caso A: o educador pede a ativação de uma instituição da base que ainda não está no HQ-Lab.
 * A instituição nasce "solicitada" com os dados oficiais (ninguém digita o nome), e o vínculo dele
 * nasce pendente. Quem ativa é o admin geral, ao aprovar esse primeiro educador.
 */
export async function pedirAtivacao(
  base: InstituicaoDaBase,
  pessoa: { uid: string; nome?: string; email?: string }
): Promise<Resultado> {
  try {
    const existente = await buscarInstituicao(base.id);
    if (existente?.situacao === "recusada") {
      // 08/10: instituição recusada pode ser reivindicada de novo, em nome próprio.
      await Firebase.updateDocument("instituicoes", base.id, {
        situacao: "solicitada",
        solicitanteUid: pessoa.uid,
        solicitanteEmail: pessoa.email ?? null,
      });
    }
    if (!existente) {
      await Firebase.setDocument("instituicoes", base.id, {
        nome: base.nome,
        apelido: base.sigla || null,
        cidade: base.municipio,
        uf: "BA",
        nivel: base.nivel,
        rede: base.rede,
        situacao: "solicitada",
        solicitanteUid: pessoa.uid,
        solicitanteEmail: pessoa.email ?? null,
        criadaEm: new Date().toISOString(),
      });
    }
    const r = await solicitarVinculo({
      instituicaoId: base.id,
      instituicaoNome: base.nome,
      uid: pessoa.uid,
      nome: pessoa.nome,
      email: pessoa.email,
      papel: "educador",
    });
    return { ok: r.ok, erro: r.erro };
  } catch (e) {
    return { ok: false, erro: legivel(e, "pedir a ativação desta instituição") };
  }
}

/* ── o admin geral ── */

export async function instituicoesNaSituacao(situacao: SituacaoInstituicao): Promise<Instituicao[]> {
  try {
    const docs = await Firebase.getCollection("instituicoes", [where("situacao", "==", situacao)], 500);
    return (docs as Instituicao[]).sort((a, b) => (a.criadaEm ?? "").localeCompare(b.criadaEm ?? ""));
  } catch {
    return [];
  }
}

/** Caso A, do lado do admin: aprovar o 1º educador ATIVA a instituição e o faz moderador. */
export async function ativarPeloEducador(
  instituicaoId: string,
  vinculoDoEducador: Vinculo,
  adminUid: string
): Promise<Resultado> {
  try {
    const agora = new Date().toISOString();
    await Firebase.updateDocument("instituicoes", instituicaoId, {
      situacao: "ativa",
      ativadaEm: agora,
      ativadaPor: adminUid,
    });
    await Firebase.updateDocument("vinculos", vinculoDoEducador.id, {
      situacao: "ativo",
      moderadoPor: adminUid,
      moderadoEm: agora,
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, erro: legivel(e, "ativar esta instituição") };
  }
}

/** Recusa o pedido de ativação: a instituição fica "recusada" e os pedidos de educador também. */
export async function recusarAtivacao(
  instituicaoId: string,
  pedidos: Vinculo[],
  adminUid: string
): Promise<Resultado> {
  try {
    const agora = new Date().toISOString();
    await Firebase.updateDocument("instituicoes", instituicaoId, {
      situacao: "recusada",
      ativadaPor: adminUid,
    });
    for (const v of pedidos) {
      await Firebase.updateDocument("vinculos", v.id, {
        situacao: "recusado",
        moderadoPor: adminUid,
        moderadoEm: agora,
        recusadoEmMs: Date.now(),
      });
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, erro: legivel(e, "recusar esta instituição") };
  }
}

/** Suspender (ativa -> suspensa) e reativar (suspensa -> ativa). Só o admin geral. */
export async function mudarSituacaoDaInstituicao(
  instituicaoId: string,
  situacao: "ativa" | "suspensa",
  adminUid: string
): Promise<Resultado> {
  try {
    await Firebase.updateDocument("instituicoes", instituicaoId, { situacao, ativadaPor: adminUid });
    return { ok: true };
  } catch (e) {
    return { ok: false, erro: legivel(e, "mudar a situação desta instituição") };
  }
}

/* ── convites (caso E): quem entra pelo link já entra aprovado ── */

export interface Convite {
  codigo: string;
  instituicaoId: string;
  papel: PapelInstituicao;
  criadoPor: string;
  ativo: boolean;
  criadoEm: string;
}

function novoCodigo(): string {
  const letras = "abcdefghjkmnpqrstuvwxyz23456789";
  let c = "";
  for (let i = 0; i < 10; i++) c += letras[Math.floor(Math.random() * letras.length)];
  return c;
}

export async function criarConvite(
  instituicaoId: string,
  papel: PapelInstituicao,
  educadorUid: string
): Promise<Resultado<string>> {
  const codigo = novoCodigo();
  try {
    await Firebase.setDocument("convites", codigo, {
      codigo,
      instituicaoId,
      papel,
      criadoPor: educadorUid,
      ativo: true,
      criadoEm: new Date().toISOString(),
    });
    return { ok: true, dado: codigo };
  } catch (e) {
    return { ok: false, erro: legivel(e, "criar um convite") };
  }
}

export async function convitesDaInstituicao(instituicaoId: string): Promise<Convite[]> {
  try {
    const docs = await Firebase.getCollection("convites", [where("instituicaoId", "==", instituicaoId)], 100);
    return (docs as Convite[]).sort((a, b) => (b.criadoEm ?? "").localeCompare(a.criadoEm ?? ""));
  } catch {
    return [];
  }
}

export async function desativarConvite(codigo: string): Promise<Resultado> {
  try {
    await Firebase.updateDocument("convites", codigo, { ativo: false });
    return { ok: true };
  } catch (e) {
    return { ok: false, erro: legivel(e, "desativar este convite") };
  }
}

export async function lerConvite(codigo: string): Promise<Convite | null> {
  try {
    return ((await Firebase.getDocument("convites", codigo)) as Convite | null) ?? null;
  } catch {
    return null;
  }
}

/** Entrar pelo convite: o vínculo nasce ATIVO, porque gerar o link foi a aprovação do educador. */
export async function entrarPorConvite(
  convite: Convite,
  pessoa: { uid: string; nome?: string; email?: string }
): Promise<Resultado> {
  try {
    await Firebase.setDocument("vinculos", idDoVinculo(convite.instituicaoId, pessoa.uid), {
      instituicaoId: convite.instituicaoId,
      uid: pessoa.uid,
      nome: pessoa.nome ?? null,
      email: pessoa.email ?? null,
      papel: convite.papel,
      situacao: "ativo" as SituacaoVinculo,
      origem: "convite",
      convite: convite.codigo,
      criadoEm: new Date().toISOString(),
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, erro: legivel(e, "entrar por este convite") };
  }
}
